/**
 * Shared implementation behind the typed input primitives (`<caos-text-field>`,
 * `<caos-number-field>`, `<caos-date-field>`). One template, one appearance — the subclass only
 * sets the control it wants and the type-specific attributes it forwards, so the fields differ by
 * props, never identity (the closed-catalog rule).
 *
 * CAOS-600 — the control is no longer fixed at construction. A field's declared FORMAT can change
 * which control draws it: a Text field in the `long` format is a text area, a Date field editing a
 * `Time` value is a time input, a Number field in the `slider` format is a range. Each of those is
 * the same field with the same label, value, validity and message — a different control drawn for
 * a declared format, which is exactly the distinction that keeps them from becoming four more
 * components. `swapControl` below is what makes that possible without rebuilding the element, and
 * it is careful about it: the value, the id the label points at, the `part` seam and the listeners
 * all survive the swap, because a control that loses any of them has quietly become a new field.
 * (Catalog: Internal › BaseField — the shared implementation behind the single-line input primitives; not an element of its own.)
 *
 * States (both fields): label, placeholder, disabled, invalid + validation message,
 * required, and the accent focus ring. Every colour/space/radius reads a `--caos-*`
 * token. Styling seams: `::part(label)`, `::part(input)`, `::part(message)`.
 */
import { CaosElement, makeSheet } from '../base-element.js';

export const fieldSheet = makeSheet(`
  :host { display: block; }

  .field { display: flex; flex-direction: column; gap: var(--caos-space-1); }

  /* FORM/edit label — Inter 600, 14px (Dragon §08). */
  .label {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-weight: 600;
    color: var(--caos-color-text-muted);
  }
  .label:empty { display: none; }
  .req { color: var(--caos-color-danger); margin-left: 2px; }

  /* Boxed form input — Dragon input: 2px radius, sunken surface, 12×14 padding.
     CAOS-600 — a text area is the same box; it says so here rather than growing a second
     appearance, because multi-line is a FORMAT of this field and not another field. */
  input,
  textarea {
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: var(--caos-space-3) 14px;
    border-radius: var(--caos-radius);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.4;
    transition: border-color 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
  }
  input::placeholder,
  textarea::placeholder { color: var(--caos-color-text-muted); opacity: 0.85; }

  /* The multi-line control: several rows tall, resizable by the reader down its own axis only
     (a text area that can be dragged wider tears the form's column out of shape). */
  textarea {
    min-height: 96px;
    resize: vertical;
  }

  input:hover:not(:disabled):not(:focus),
  textarea:hover:not(:disabled):not(:focus) { border-color: color-mix(in srgb, var(--caos-color-accent) 45%, var(--caos-color-border)); }

  /* Focus = 5% accent wash + soft strong border, NOT a solid focus ring (Dragon §08). */
  input:focus-visible,
  input:focus,
  textarea:focus-visible,
  textarea:focus {
    outline: none;
    border-color: var(--caos-color-border-strong);
    box-shadow: inset 0 0 0 100px var(--caos-accent-wash);
  }

  input:disabled,
  textarea:disabled {
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text-muted);
    cursor: not-allowed;
    opacity: var(--caos-state-disabled-opacity);
  }

  :host([invalid]) input,
  :host([invalid]) textarea {
    border-color: var(--caos-color-danger);
  }
  :host([invalid]) input:focus-visible,
  :host([invalid]) input:focus,
  :host([invalid]) textarea:focus-visible,
  :host([invalid]) textarea:focus {
    border-color: var(--caos-color-danger);
    box-shadow: inset 0 0 0 100px color-mix(in srgb, var(--caos-color-danger) 6%, transparent);
  }

  .message {
    font-family: var(--caos-font);
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
  }
  .message:empty { display: none; }
  :host([invalid]) .message { color: var(--caos-color-danger); }

  /* numeric values read in tabular figures so they align */
  :host([numeric]) input { font-variant-numeric: tabular-nums; }

  /* ── CAOS-600 — the slider presentation of the number field. A range control is not a box
     with a value typed into it, so it drops the border, the surface and the padding the boxed
     input wears, and the chosen number is drawn BESIDE it. That readout is not decoration: a
     range input shows a position and never a value, so a slider without it is a control that
     cannot tell you what it is set to. It reads in tabular figures so it stops jittering as
     the number changes width while being dragged. */
  .readout { display: none; }
  :host([presentation="slider"]) .row {
    display: flex;
    align-items: center;
    gap: var(--caos-space-3);
  }
  :host([presentation="slider"]) input[type="range"] {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    accent-color: var(--caos-color-accent);
  }
  :host([presentation="slider"]) input[type="range"]:focus-visible {
    outline: 2px solid var(--caos-color-border-strong);
    outline-offset: 2px;
    box-shadow: none;
  }
  :host([presentation="slider"]) .readout {
    display: inline-block;
    min-width: 3ch;
    text-align: right;
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-variant-numeric: tabular-nums;
    color: var(--caos-color-text);
  }

  /* ── Inline (record-detail) variant — the P5b in-place editor. No box: a
     transparent field with a 1px accent underline that inherits the surrounding
     font (matches CrmField). Turned on by the \`inline\` attribute; the boxed form
     rendering above stays the default for create/edit forms. */
  :host([inline]) { display: inline-block; width: 100%; }
  :host([inline]) .field { gap: 0; }
  :host([inline]) .label { display: none; }
  :host([inline]) .message { display: none; }
  :host([inline]) input,
  :host([inline]) textarea {
    min-height: 0;
    padding: 0 0 2px 0;
    border: none;
    border-bottom: 1px solid var(--caos-color-accent);
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
    line-height: inherit;
    color: inherit;
  }
  :host([inline]) input:hover:not(:disabled):not(:focus),
  :host([inline]) textarea:hover:not(:disabled):not(:focus) { border-bottom-color: var(--caos-color-accent); }
  :host([inline]) input:focus-visible,
  :host([inline]) input:focus,
  :host([inline]) textarea:focus-visible,
  :host([inline]) textarea:focus {
    border-color: transparent;
    border-bottom-color: var(--caos-color-accent);
    background: transparent;
    box-shadow: none;
  }
`);

/**
 * Every field needs an id its own label can point at. Without one the label and the control
 * are two unrelated nodes: a screen reader announces an unnamed text box, and clicking the
 * label does not focus the field (CAOS-587 review).
 */
let fieldUid = 0;

/**
 * Base class. Subclasses pass their input `type` and the list of numeric-style
 * attributes (min/max/step) they forward to the inner input.
 */
/** The controls a field may draw. Each is a presentation of ONE field, never a second field. */
export type CaosFieldControl = 'input' | 'textarea';

export abstract class CaosBaseField extends CaosElement {
  /**
   * The control currently drawn. Typed as the union rather than `HTMLInputElement` because a
   * declared format can swap it — see {@link swapControl}. Everything the field does to it
   * (value, disabled, required, name, the aria state) is shared by both; the few things that are
   * NOT (`type`, `min`/`max`/`step`) are guarded at their one use below.
   */
  protected input: HTMLInputElement | HTMLTextAreaElement;
  /** The input `type` to restore when swapping back from a text area to a single-line control. */
  private inputType: string;
  /** The line the control sits on, so a presentation can draw something beside it. */
  protected row!: HTMLDivElement;
  private labelEl: HTMLLabelElement;
  private reqEl: HTMLSpanElement;
  private messageEl: HTMLDivElement;

  static get observedAttributes(): string[] {
    return ['label', 'placeholder', 'value', 'disabled', 'required', 'invalid', 'message', 'name', 'min', 'max', 'step', 'maxlength'];
  }

  protected constructor(inputType: string) {
    super(fieldSheet);
    this.inputType = inputType;
    const field = document.createElement('div');
    field.className = 'field';

    this.labelEl = document.createElement('label');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');
    this.reqEl = document.createElement('span');
    this.reqEl.className = 'req';
    this.reqEl.textContent = '*';
    this.reqEl.hidden = true;
    this.reqEl.setAttribute('aria-hidden', 'true');
    this.labelEl.append(this.reqEl);

    this.input = document.createElement('input');
    this.input.type = inputType;
    this.input.id = `caos-field-${++fieldUid}`;
    this.labelEl.htmlFor = this.input.id;
    this.input.setAttribute('part', 'input');
    this.input.addEventListener('input', () => {
      this.setAttribute('value', this.input.value);
      this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    });
    this.input.addEventListener('change', () => {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    });

    this.messageEl = document.createElement('div');
    this.messageEl.className = 'message';
    this.messageEl.setAttribute('part', 'message');

    // CAOS-600 — the control sits in a row of its own so a presentation that draws something
    // BESIDE it (the slider's value readout) has somewhere to put it. With nothing beside it the
    // row is a plain block and lays out exactly as the bare control did.
    this.row = document.createElement('div');
    this.row.className = 'row';
    this.row.append(this.input);

    field.append(this.labelEl, this.row, this.messageEl);
    this.root.append(field);
  }

  connectedCallback(): void {
    this.sync();
  }

  /**
   * Draw this field with a different control, keeping everything that makes it the same field.
   *
   * The generated id the label's `for` points at, the `part="input"` styling seam and both listeners
   * move to the new node. Dropping any one of them is a silent regression rather than a visible one:
   * a lost id unnames the control for a screen reader and stops a label click focusing it, a lost
   * `part` un-styles whatever a package had themed, and a lost listener makes the field stop
   * reporting changes while still looking exactly right.
   *
   * The VALUE is not carried across, and does not need to be: the `value` attribute is this field's
   * source of truth — the input listener writes every keystroke back to it — and the shared pass
   * below writes it onto whichever control is now drawn. Copying it here as well looked like the
   * careful thing to do and was untestable, because removing the copy changed nothing observable.
   *
   * A no-op when the control asked for is already the one drawn, so a re-render does not blow away
   * the node the reader is typing into.
   */
  protected swapControl(kind: CaosFieldControl): void {
    const current = this.input.tagName === 'TEXTAREA' ? 'textarea' : 'input';
    if (current === kind) return;
    const old = this.input;
    const next =
      kind === 'textarea' ? document.createElement('textarea') : document.createElement('input');
    if (next instanceof HTMLInputElement) next.type = this.inputType;
    next.id = old.id;
    next.setAttribute('part', 'input');
    next.addEventListener('input', () => {
      this.setAttribute('value', this.input.value);
      this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    });
    next.addEventListener('change', () => {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    });
    // Whether the reader was IN the control decides whether they stay in it. Read before the swap,
    // because afterwards the old node is detached and nothing is focused at all.
    //
    // Asked of the DOCUMENT rather than of the shadow root. Focus inside a shadow root is retargeted,
    // so the document reports the HOST as active whenever any of its children has focus — and this
    // field has exactly one focusable child, so that is an answer. Asking the shadow root which of its
    // children is active is the more direct question and is not safe to ask: it resolves the
    // document's active element against this tree, and throws outright when the active element is in
    // another tree, which is every ordinary case of a field being re-rendered while somebody types in
    // a different one.
    const wasFocused = this.ownerDocument.activeElement === this;
    old.replaceWith(next);
    this.input = next;
    if (wasFocused) next.focus();
    // Deliberately does NOT sync: every caller swaps from inside its own `sync`, and syncing here
    // would run the shared pass twice for one attribute change.
  }

  /**
   * Draw this field with a different input `type` — the date field's answer to the temporal kinds,
   * which are three presentations of one calendar control rather than three components. A no-op
   * when nothing changes, for the same reason {@link swapControl} is.
   */
  protected setInputType(type: string): void {
    if (this.inputType === type) return;
    this.inputType = type;
    if (this.input instanceof HTMLInputElement) this.input.type = type;
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** Current value of the control. */
  get value(): string {
    return this.input.value;
  }
  set value(v: string) {
    this.setAttribute('value', v);
  }

  /** Focus the inner control (used by the inline record-detail editor to auto-focus
   *  when a field's value is clicked to edit); selects text where the control allows. */
  override focus(options?: FocusOptions): void {
    this.input.focus(options);
    try {
      this.input.select();
    } catch {
      /* number/date inputs don't support text selection — ignore */
    }
  }

  protected sync(): void {
    // label (prepend text before the required marker)
    const labelText = this.getAttribute('label') ?? '';
    this.labelEl.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) n.remove();
    });
    if (labelText) this.labelEl.insertBefore(document.createTextNode(labelText), this.reqEl);
    this.reqEl.hidden = !this.hasAttribute('required');

    const placeholder = this.getAttribute('placeholder');
    if (placeholder !== null) this.input.setAttribute('placeholder', placeholder);
    else this.input.removeAttribute('placeholder');

    const value = this.getAttribute('value');
    if (value !== null && this.input.value !== value) this.input.value = value;

    this.input.disabled = this.hasAttribute('disabled');
    this.input.required = this.hasAttribute('required');
    if (this.hasAttribute('invalid')) this.input.setAttribute('aria-invalid', 'true');
    else this.input.removeAttribute('aria-invalid');

    const name = this.getAttribute('name');
    if (name !== null) this.input.name = name;

    // CAOS-452 - the declared length, forwarded to BOTH control kinds because `maxlength` is read
    // on an input and on a text area alike, and the format that picks between them is not what
    // decides how long the value may be. The kernel's Field carries `length` on the Text kind and
    // nothing drew it, so a field declaring a limit accepted more than it could store and the
    // value was cut on the way down rather than refused on the way in.
    //
    // Set as an ATTRIBUTE rather than via `.maxLength`, because the property coerces an
    // unparseable value to -1 and an attribute that is absent and an attribute that is nonsense
    // are then indistinguishable in the DOM.
    const maxlength = this.getAttribute('maxlength');
    if (maxlength !== null) this.input.setAttribute('maxlength', maxlength);
    else this.input.removeAttribute('maxlength');

    // `min`/`max`/`step` are an input's attributes and mean nothing on a text area, so they are
    // forwarded only where they are read. Setting them on a textarea would be inert rather than
    // wrong, but it would also be a lie in the DOM about what constrains the value.
    if (this.input instanceof HTMLInputElement) {
      for (const attr of ['min', 'max', 'step'] as const) {
        const v = this.getAttribute(attr);
        if (v !== null) this.input.setAttribute(attr, v);
        else this.input.removeAttribute(attr);
      }
    }

    this.messageEl.textContent = this.getAttribute('message') ?? '';
  }
}
